import { ARENA } from '../../config/constants.js';

const PLANT_RANGE = 90; // stops approaching and plants once this close
const ARM_DELAY = 0.7; // telegraph window before the mine goes off — same "readable" language as every other attack
const REPLANT_COOLDOWN = 2.2; // resumes chasing for this long after detonating before it can plant again
const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * A minion that closes to short range, plants, telegraphs an arm window,
 * then detonates a mine underneath itself — reusing the exact
 * `minion:exploded` event Bomber.js emits (handled generically in Game.js's
 * _wireJuice off x/y/damage/radius, not tied to Bomber specifically), so no
 * Game.js changes were needed to wire this up. Unlike Bomber, detonating
 * doesn't kill it — it's a repeatable hazard-planter that has to be chased
 * down and killed like a normal minion to stop the mine spam, which is what
 * makes it a zone-control threat via terrain instead of via a bullet.
 */
export class Trapper {
  constructor(eventBus, {
    x, y, radius = 6, hp, moveSpeed, contactDamage, mineDamage, mineRadius,
  }) {
    this.eventBus = eventBus;
    this.type = 'trapper';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.mineDamage = mineDamage;
    this.mineRadius = mineRadius;
    this._contactCooldown = 0;
    this.isDead = false;
    this.hitFlashTimer = 0;

    this._state = 'approach'; // approach -> armed -> (back to) approach, gated by _cooldownTimer
    this._armTimer = 0;
    this._cooldownTimer = 0;
  }

  get isTelegraphing() {
    return this._state === 'armed';
  }

  update(dt, player, _bulletPool) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;

    if (this._state === 'approach') {
      if (this._cooldownTimer > 0) {
        this._cooldownTimer -= dt;
      }
      if (dist > PLANT_RANGE || this._cooldownTimer > 0) {
        this.x += (dx / dist) * this.moveSpeed * dt;
        this.y += (dy / dist) * this.moveSpeed * dt;
        this._clampToArena();
      } else {
        this._state = 'armed';
        this._armTimer = ARM_DELAY;
      }
    } else {
      this._armTimer -= dt;
      if (this._armTimer <= 0) {
        this.eventBus.emit('minion:exploded', { x: this.x, y: this.y, damage: this.mineDamage, radius: this.mineRadius });
        this._state = 'approach';
        this._cooldownTimer = REPLANT_COOLDOWN;
      }
    }

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
